import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  DatePicker,
  Space,
  Card,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DynamicForm from "./DynamicForm";
import EditorComponent from "../Subjects/Component/AdminEditor";

const { RangePicker } = DatePicker;

const JobForm = ({ initialValues = {}, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const [editorContent, setEditorContent] = useState("");

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        skills: initialValues.skills ? initialValues.skills.join(", ") : "",
        availableRange:
          initialValues.availableFrom && initialValues.availableTo
            ? [
                dayjs(initialValues.availableFrom),
                dayjs(initialValues.availableTo),
              ]
            : undefined,
      });
      setEditorContent(initialValues.description || "");
    }
  }, [initialValues]);

  const handleFinish = (values) => {
    const formattedValues = {
      ...values,
      skills: values.skills
        ? values.skills.split(",").map((skill) => skill.trim())
        : [],
      salary: {
        min: values.salaryMin,
        max: values.salaryMax,
        currency: values.currency || "USD",
      },

      experience: {
        min: values.expMin,
        max: values.expMax,
      },

      availableFrom: values.availableRange?.[0],
      availableTo: values.availableRange?.[1],
      description: editorContent,

      // 🔥 Proper rounds formatting
      rounds:
        values.rounds?.map((round, index) => ({
          name: round.name,
          description: round.description,
          order: index + 1,
        })) || [],
    };

    delete formattedValues.salaryMin;
    delete formattedValues.salaryMax;
    delete formattedValues.currency;
    delete formattedValues.expMin;
    delete formattedValues.expMax;
    delete formattedValues.availableRange;

    onSubmit(formattedValues);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      {/* BASIC INFO */}
      <Form.Item
        label="Job Title"
        name="title"
        rules={[{ required: true, message: "Title is required" }]}
      >
        <Input />
      </Form.Item>

      <div style={{ marginBottom: 24 }}>
        <Form.Item label="Description">
          <EditorComponent
            assignmentLabel="Description"
            hideInput={true}
            editorContent={editorContent}
            onEditorChange={(content) => {
              setEditorContent(content);
            }}
            inputPlaceHolder="Type here"
            isCreateQuestion={false}
            
          />
        </Form.Item>
      </div>

      <Form.Item label="Location" name="location">
        <Input />
      </Form.Item>

      <Form.Item label="Employment Type" name="employmentType">
        <Select>
          <Select.Option value="full-time">Full Time</Select.Option>
          <Select.Option value="part-time">Part Time</Select.Option>
          <Select.Option value="contract">Contract</Select.Option>
          <Select.Option value="internship">Internship</Select.Option>
          <Select.Option value="temporary">Temporary</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="Workplace Type" name="workplaceType">
        <Select>
          <Select.Option value="onsite">Onsite</Select.Option>
          <Select.Option value="remote">Remote</Select.Option>
          <Select.Option value="hybrid">Hybrid</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="Department" name="department">
        <Input />
      </Form.Item>

      <Space size="large">
        <Form.Item label="Salary Min" name="salaryMin">
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        <Form.Item label="Salary Max" name="salaryMax">
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        <Form.Item label="Currency" name="currency" initialValue="USD">
          <Input style={{ width: 150 }} />
        </Form.Item>
      </Space>

      <Space size="large">
        <Form.Item label="Experience Min (Years)" name="expMin">
          <InputNumber style={{ width: 150 }} />
        </Form.Item>

        <Form.Item label="Experience Max (Years)" name="expMax">
          <InputNumber style={{ width: 150 }} />
        </Form.Item>
      </Space>

      <Form.Item label="Education" name="education">
        <Input />
      </Form.Item>

      <Form.Item label="Skills" name="skills">
        <Input placeholder="React, Node.js, MongoDB" />
      </Form.Item>

      <Form.Item label="Openings" name="openings" initialValue={1}>
        <InputNumber min={1} />
      </Form.Item>

      <Form.Item label="Available Duration" name="availableRange">
        <RangePicker />
      </Form.Item>

      <Form.Item label="Status" name="status" initialValue="draft">
        <Select>
          <Select.Option value="draft">Draft</Select.Option>
          <Select.Option value="published">Published</Select.Option>
          <Select.Option value="closed">Closed</Select.Option>
        </Select>
      </Form.Item>

      {/* 🔥 ROUNDS SECTION ADDED ONLY */}
      <Card title="Interview Rounds" style={{ marginBottom: 24 }}>
        <Form.List name="rounds">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Card
                  key={key}
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={`Round ${index + 1}`}
                  extra={
                    <DeleteOutlined
                      onClick={() => remove(name)}
                      style={{ color: "red" }}
                    />
                  }
                >
                  <Form.Item
                    {...restField}
                    name={[name, "name"]}
                    label="Round Name"
                    rules={[{ required: true, message: "Round name required" }]}
                  >
                    <Input placeholder="HR Round / Technical Round" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "description"]}
                    label="Description"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Card>
              ))}

              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add Round
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <Form.Item
        label="Application Form Builder"
        name="formTemplate"
        rules={[{ required: true, message: "Form template required" }]}
      >
        <DynamicForm
          value={form.getFieldValue("formTemplate")}
          onChange={(val) => {
            form.setFieldsValue({ formTemplate: val });
          }}
        />
      </Form.Item>

      <Button
        className="btn-gradient"
        type="primary"
        htmlType="submit"
        loading={loading}
      >
        Save Job
      </Button>
    </Form>
  );
};

export default JobForm;




